import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { CompanyModel } from '@one/models/company.model';
import { SKIP_AUTH, SKIP_TIMEZONE } from '@one/utils/http-context.utils';
import { catchError, map, Observable, of, shareReplay, timeout } from 'rxjs';

/** Quanto o SSR espera pela empresa. Passou disso, o HTML sai genérico (a página nunca cai). Mesmo valor do PUBLIC_THEME_SSR_TIMEOUT_MS do plano 3. */
export const SERVER_COMPANY_TIMEOUT_MS = 800;

export interface IServerCompanyResult {
  /** Empresa do endpoint leve GET /api/company/{slug} (CompanyViewModel). null em 404, erro ou timeout. */
  company: CompanyModel | null;
  /** true só quando a API respondeu 404 (slug não existe ou empresa apagada). */
  notFound: boolean;
}

const NOTHING: IServerCompanyResult = { company: null, notFound: false };

/**
 * Empresa da URL buscada UMA vez por requisição de SSR e compartilhada por quem precisar no servidor
 * (prévia de link deste plano; tema do plano 3). No navegador não faz nada: lá quem busca é o CompanyService (/complete).
 *
 * Escopo: providedIn 'root' no servidor = uma instância por requisição (cada SSR sobe uma aplicação nova).
 * transferCache: false — a resposta NÃO vai para o <script id="ng-state">. O navegador não usa este endpoint,
 * e o JSON traz taxIdNumber (CPF/CNPJ do dono), e-mail e chave PIX, que não podem ir para o HTML.
 */
@Injectable({ providedIn: 'root' })
export class ServerCompanyService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly isServer: boolean = isPlatformServer(inject(PLATFORM_ID));
  private readonly requests: Map<string, Observable<IServerCompanyResult>> = new Map();

  public load(slug: string): Observable<IServerCompanyResult> {
    if (!this.isServer || !slug) return of(NOTHING);

    const cached: Observable<IServerCompanyResult> | undefined = this.requests.get(slug);
    if (cached) return cached;

    const request$: Observable<IServerCompanyResult> = this.http
      .get<CompanyModel | null>(`${environment.apiUrl}/company/${encodeURIComponent(slug)}`, {
        // Sem TimeZoneOffset (o endpoint não usa) e sem Authorization (o servidor não tem sessão). Sem o SKIP_AUTH,
        // cada slug inexistente passaria pelo catchError do authInterceptor e viraria warning de log.
        context: new HttpContext().set(SKIP_TIMEZONE, true).set(SKIP_AUTH, true),
        transferCache: false
      })
      .pipe(
        timeout({ first: SERVER_COMPANY_TIMEOUT_MS }),
        map((company: CompanyModel | null): IServerCompanyResult => ({ company: company?.slug ? company : null, notFound: false })),
        catchError((error: unknown) => {
          const notFound: boolean = error instanceof HttpErrorResponse && error.status === 404;
          // Log do servidor (CloudWatch da Lambda do Front): mede quantos SSR saem sem prévia por timeout ou erro.
          // eslint-disable-next-line no-console
          if (!notFound) console.warn(`[ssr] empresa não carregada para a prévia: ${slug} (${describeError(error)})`);
          return of({ company: null, notFound });
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.requests.set(slug, request$);
    return request$;
  }
}

function describeError(error: unknown): string {
  if (error instanceof HttpErrorResponse) return `HTTP ${error.status}`;
  if (error instanceof Error) return error.name;
  return 'erro desconhecido';
}
